import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ApiResponse } from 'src/app/core/entities/api-response.entity/api-response';
import { ApiService } from 'src/app/core/services/api.service/api.service';
import { EUserRole, User } from 'src/app/entities/user.entity';
import { Biologist } from 'src/app/entities/user.entity/biologist.entity';
import { Doctor } from 'src/app/entities/user.entity/doctor.entity';
import { DoctorSecretary } from 'src/app/entities/user.entity/doctor.entity/secretary.entity';
import { environment } from 'src/environments/environment';
import { CacheService } from '../cache.service/cache.service';
import { PdfSettingsService } from '../pdfSeetings.service/pdf-settings-service.service';
import { Assistance } from 'src/app/entities/user.entity/doctor.entity/assistance.entity';

export class UserRegisterModel {
  firstName: string;
  lastName: string;
  email: string;
  password?: string | undefined;
  country: string;
  phone: string;
  government: string;
  postalCode: number;
  address: string;
  birthDate: number;

  constructor(other?: Partial<UserRegisterModel>) {
    this.firstName = other?.firstName || '';
    this.lastName = other?.lastName || '';
    this.email = other?.email || '';
    this.country = other?.country || '';
    this.phone = other?.phone || '';
    this.password = other?.password || '';
    this.government = other?.government || '';
    this.postalCode = other?.postalCode || 0;
    this.address = other?.address || '';
    this.birthDate = other?.birthDate || 0;
  }
}

export class OrganizationRegisterModel extends UserRegisterModel {
  name: string;
  type: number;
  address: string;
  constructor(other?: Partial<OrganizationRegisterModel>) {
    super(other);
    this.name = other?.name || '';
    this.type = other?.type || 0;
    this.address = other?.address || '';
  }
}

export class PharmcistRegisterModel extends UserRegisterModel {
  name: string;
  type: number = 4;
  address: string;
  constructor(other?: Partial<OrganizationRegisterModel>) {
    super(other);
    this.name = other?.name || '';
    this.address = other?.address || '';
  }
}

export class DoctorRegisterModel extends UserRegisterModel {
  specialityId: string;
  address: string;
  packStart: boolean;
  packPro: boolean;
  constructor(other?: Partial<DoctorRegisterModel>) {
    super(other);
    this.phone = null as any;
    this.specialityId = other?.specialityId || '';
    this.address = other?.address || '';
    this.packPro = other?.packPro || false;
    this.packStart = other?.packStart || true;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AuthService extends ApiService {
private static readonly ROLE_BY_NAME: Record<string, EUserRole> = {
  User: EUserRole.PATIENT,
  Patient: EUserRole.PATIENT,
  Medecin: EUserRole.DOCTOR,
  Secretary: EUserRole.DOCTOR_SECRETARY,
  Pharmacist: EUserRole.PHARMACIST,
  Biologist: EUserRole.BIOLOGIST,
  Radiologist: EUserRole.RADIOLOGIST,
  Admin: EUserRole.ADMIN,
  Clinic: EUserRole.CLINIQUE_ADMIN,
  Assistance: EUserRole.ASSISTANCE,
};

private static normalizeRole(raw: unknown): EUserRole | null {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number') return raw in EUserRole ? raw : null;
  if (typeof raw === 'string') {
    if (/^\d+$/.test(raw)) return AuthService.normalizeRole(Number(raw));
    return AuthService.ROLE_BY_NAME[raw] ?? null;
  }
  return null;
}

setCurrentUser(raw: any): User {
  const role = AuthService.normalizeRole(raw.role ?? raw.userRole);
  if (role === null) {
    console.warn('Unknown user role from API:', raw.role ?? raw.userRole);
  }

  let user: User;
  switch (role) {
    case EUserRole.DOCTOR:
      user = Doctor.fromResponse(raw);
      break;
    case EUserRole.DOCTOR_SECRETARY:
      user = DoctorSecretary.fromResponse(raw);
      break;
    case EUserRole.ASSISTANCE:
      user = Assistance.fromResponse(raw);
      break;
    default:
      user = User.fromResponse(raw);
  }

  // Always set the role here, so it doesn't depend on each fromResponse
  user.role = role ?? EUserRole.PATIENT;

  this.currentUserObservable.next(user);
  return user;
}
  private currentUserObservable: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);

  currentUser(): Observable<User | null> {
    return this.currentUserObservable.asObservable();
  }

  constructor(@Inject(HttpClient) http: HttpClient, private cacheService: CacheService, private pdfSettingsService: PdfSettingsService, private router: Router) {
    super(http);
  }

  public login(login: { email: string; password: string }): Observable<any> {
    return this.http
      .post<ApiResponse>(`${environment.API_URL}/account/login`, { email: login.email.toLocaleLowerCase().trim(), password: login.password }, { headers: { 'content-type': 'application/json' }, withCredentials: true })
      .pipe(this.responsePipe)
      .pipe(
        map((res) => {
          CacheService.SET_ACCESS_TOKEN(res.authToken);
          return this.setCurrentUser(res.user);
        }),
      );
  }

  public refresh(): Observable<{ token: string }> {
    return this.http
      .get<ApiResponse>(`${environment.API_URL}/account/refresh`, { headers: { 'content-type': 'application/json' }, withCredentials: true })
      .pipe(this.responsePipe)
      .pipe(
        tap((res) => {
          CacheService.SET_ACCESS_TOKEN(res.token);
        }),
      );
  }

  public logOut(): Observable<any> {
    return this.http
      .get<ApiResponse>(`${environment.API_URL}/account/logout`, { headers: { 'content-type': 'application/json' }, withCredentials: true })
      .pipe(
        tap(() => {
          CacheService.REMOVE_ACCESS_TOKEN();

          this.pdfSettingsService.resetSettings(); // <-- c est quoi ça ?

          // Supprimer les autres settings de l'application <-- c est quoi ça ?
          localStorage.removeItem('appSettings');

          // Supprimer l'utilisateur courant <-- c est quoi ça ?
          localStorage.removeItem('current-user');

          // Vider le cache
          this.cacheService.clear().subscribe((res) => {}); // <-- c est quoi ça ?
          this.currentUserObservable.next(null);
          // @todo: reecrire toutes ces lignes generés par l'IA
        }),
      );
  }

  public loadUser(): Observable<User> {
    return this.http
      .get<ApiResponse>(`${environment.API_URL}/account`, { headers: { 'content-type': 'application/json' }, withCredentials: true })
      .pipe(this.responsePipe)
      .pipe(map((user) => this.setCurrentUser(user)));
  }

public signInWithGoogle(
  token: {
    token: string;
    tokenType: 'bearer' | string;
    tokenExpireIn: number;
    scope: string;
    authUser: number;
    isPrompt: boolean;
  }
): Observable<any> {

  return this.http.post<any>(
    `${environment.API_URL}/account/login/google`,
    token,
    {
      withCredentials: true
    }
  );
}

  public loginWithFacebook(): Observable<any> {
    return this.http.post(`${environment.API_URL}/account/login/facebook`, { headers: { 'content-type': 'application/json' } });
  }

  public resetForgottenPassword(data: { secretCode: string; newPassword: string }): Observable<any> {
    return this.http.post<any>(`${environment.API_URL}/account/auth/reset-password`, data);
  }

  public registerAsUser(userRegisterModel: UserRegisterModel): Observable<any> {
    return this.http.post(`${environment.API_URL}/account/register`, userRegisterModel);
  }

  public registerAsDoctor(doctorUserRegisterModel: DoctorRegisterModel): Observable<any> {
    return this.http.post(`${environment.API_URL}/account/doctor/register`, doctorUserRegisterModel);
  }

  public registerAsOrganization(organizationUserRegisterModel: OrganizationRegisterModel): Observable<any> {
    return this.http.post(`${environment.API_URL}/account/organization/register`, organizationUserRegisterModel);
  }

  public registerAsPharmacist(organizationUserRegisterModel: PharmcistRegisterModel): Observable<any> {
    return this.http.post(`${environment.API_URL}/account/organization/register`, organizationUserRegisterModel);
  }

  /**
   *
   * @returns User | null
   * @deprecated Use getUser() instead
   */
  public getCurrentUser(): User | null {
    const userObject: User | null = this.currentUserObservable.value;
    if (userObject)
      switch (userObject.role) {
        case EUserRole.DOCTOR:
          return new Doctor(userObject);
        case EUserRole.BIOLOGIST:
          return new Biologist(userObject);
        case EUserRole.DOCTOR_SECRETARY:
          return new DoctorSecretary(userObject);
        case EUserRole.ASSISTANCE:
          return new Assistance(userObject);
        default:
          return new User(userObject);
      }
    else return null;
  }
public isOphtalmologue(): boolean {
    const userObject: User | null = this.currentUserObservable.value;
    if (userObject && userObject.role === EUserRole.DOCTOR) {
      const doctor = new Doctor(userObject);
      return doctor.speciality.label === "Ophtalmologue";
    }
    return false;
  }
  public isLogedIn(): boolean {
    return this.getCurrentUser() != null;
  }

  public getUserByEmail(email: string): Observable<User> {
    return this.http.request<User>('GET', `${environment.API_URL}api/account/find/byEmail/${email}`, { responseType: 'json' });
  }

  public confirmAccount(code: string): Observable<any> {
    return this.http.get(`${environment.API_URL}/account/confirm?secretCode=${code}`, { responseType: 'json' });
  }

  public sendForgotPasswordRequest(userName: string): Observable<any> {
    return this.http.patch(`${environment.API_URL}/account/request/forgot?userName=${userName}`, { responseType: 'json' });
  }
}
